import StrikeZoneModelBase from "./StrikeZoneModelBase.js";

/**
 * This is the strike zone boundary dimensions and location drawn to the rulebook, with the added half-ball width added to the perimeter.
 * Because if the center of the ball lands with half its width outside the strike zone boundary,the ball’s outer edge touches the zone.
 * This is the strike zone that determines true balls and strikes. If the center of the ball is inside this zone, it is a strike.
 * more @ https://www.baseballprospectus.com/news/article/40891/prospectus-feature-the-universal-strike-zone/
 */
class TrueStrikeZone extends StrikeZoneModelBase{
    constructor(ballDiameterM, homePlateWidthM, StrikeZoneBottomM, StrikeZoneTopM){
        super();

        this.setBallDiameter(ballDiameterM);
        this.setHomePlateWidth(homePlateWidthM);
        this.setStrikeZoneHeight(StrikeZoneBottomM, StrikeZoneTopM);
    }

    /**
     * returns top offset of the strike zone in meters from the home plate level
     */
    getStrikeZoneTop(){
        return this.strikeZoneTop + this.ballDiameter/2;
    }

    /**
     * returns bottom offset of the strike zone in meters from the home plate level
     */
    getStrikeZoneBottom(){
        return this.strikeZoneBottom - this.ballDiameter/2;
    }

    /**
     * returns left offset of the strike zone in meters from umpire's/catcher's perspective from the center of the home plate
     */
    getStrikeZoneLeft(){
        return this.strikeZoneLeft + this.ballDiameter / -2;
    }

    /**
     * returns left right of the strike zone in meters from umpire's/catcher's perspective from the center of the home plate
     */
    getStrikeZoneRight(){
        return this.strikeZoneRight + this.ballDiameter / 2;
    }

    setBallDiameter(diameterInMeters){
        if ( typeof(diameterInMeters) === 'undefined') {
            // Under the current rules, a major league baseball weighs between 5 and 5 1⁄4 ounces (142 and 149 g),
            // and is 9 to 9 1⁄4 inches (229–235 mm) in circumference (2 7⁄8–3 in or 73–76 mm in diameter).
            // see also https://www.umpirebible.com/index.php/rules-pitching/calling-balls-strikes
            this.ballDiameter = 3 * 0.0254;
        }
        else if ( typeof(diameterInMeters) !== 'number') {
            throw new TypeError('ball diameter must contain valid number');
        }
        else if (diameterInMeters < 0){
            throw new RangeError('ball diameter cannot be negative');
        }
        else{
            this.ballDiameter = diameterInMeters;
        }

        return this;
    }

    setHomePlateWidth(widthInMeters){
        if ( typeof(widthInMeters) === 'undefined') {
            // The dimensions of home plate are 17 inches (43 cm) across the front, 8.5 inches (22 cm) down each side, with a triangular rear of two sides of 12 inches (30 cm).
            // The rear edges are at 45 degrees to the sides, making a point at the back. The plate is white and surrounded by a black strip 3⁄4 inch (1.9 cm) in width.
            // see also https://www.umpirebible.com/index.php/rules-pitching/calling-balls-strikes
            let homePlateWidth = 17 * 0.0254;
            this.strikeZoneLeft = homePlateWidth/-2;
            this.strikeZoneRight = homePlateWidth/2;
        }
        else if ( typeof(widthInMeters) !== 'number') {
            throw new TypeError('home plate width must contain valid number');
        }
        else if (widthInMeters < 0){
            throw new RangeError('home plate width cannot be negative');
        }
        else{
            this.strikeZoneLeft = widthInMeters/-2;
            this.strikeZoneRight = widthInMeters/2;
        }

        return this;
    }

    setStrikeZoneHeight(bottomOffsetInMeters, topOffsetInMeters){
        if ( typeof(bottomOffsetInMeters) !== 'number') {
            throw new TypeError('bottom offset must contain valid number');
        }
        else if ( typeof(topOffsetInMeters) !== 'number') {
            throw new TypeError('top offset must contain valid number');
        }
        else if (bottomOffsetInMeters < 0){
            throw new RangeError('bottom offset cannot be negative');
        }
        else if (topOffsetInMeters < 0){
            throw new RangeError('top offset cannot be negative');
        }
        else if (topOffsetInMeters < bottomOffsetInMeters){
            throw new Error('top offset cannot be less than bottom offset');
        }
        else{
            this.strikeZoneTop = topOffsetInMeters;
            this.strikeZoneBottom = bottomOffsetInMeters;
        }

        return this;
    }
}

export default TrueStrikeZone;